import { useEffect, useId, useMemo, useRef, useState, type JSX } from "react";
import type { ArchivedLeadRow } from "@gioia365/contracts";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { Button } from "../../design/components/Button";
import "../../design/components/data-table.css";
import { Dialog } from "../../design/components/Dialog";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import "../../design/components/text-field.css";
import { messageForError } from "../../i18n/errors";
import { formatCount, formatNumericDateTime } from "../../i18n/format";
import { useLocale, useT, type Translate } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { leadName } from "../lead-display";
import { SEARCH_MAX_LENGTH, useResultSearch } from "../lead-list-hooks";
import { useLeadView } from "../lead-queries";
import { archivedLeadsPagesQuery, useDeleteLead, useRestoreLead, type ArchiveBatchResult } from "./archive-queries";
import "../lead-list.css";
import "./archive.css";

// «Archiviati» (piano M3 Task 12, D9; mock-up `lead.html`, la finestra `m-trash-dialog` di `initLeadPage`): i Lead
// archiviati nel perimetro di chi guarda, dal più recente, con chi li ha archiviati, quando e perché, a pagine da 50 con
// «Mostra altri». La ricerca aspetta 200 ms dopo l'ultimo tasto e sta nell'indirizzo in un parametro suo,
// `cercaArchiviati` (decisione P16): mai il `q` dell'elenco, che resta dietro com'era. In cima la spiegazione del mock-up.
// «Ripristina» rimette un Lead, e c'è soltanto dove la riga del server dice che chi guarda lo può ripristinare (la
// modifica di quel Lead, decisione P36); «Ripristina i selezionati» quelli spuntati, e nomina con il motivo ogni Lead che
// non ha ripristinato. Un Lead unito a un altro in «Pulizia dati» dice con chi ed è fermo (questione 140). «Elimina per
// sempre i selezionati» c'è soltanto con `canDelete` (il Super admin dell'azienda): la richiesta non porta la versione,
// quindi prima di chiedere la parola «elimina» la finestra si rilegge, e si conferma sui Lead come sono adesso. Le azioni
// stanno nel piede, in vista anche con una lista lunga (decisione P25); il loro esito si legge nella finestra, perché una
// finestra modale rende inerte la pagina, avvisi compresi (D16). Un Lead in cui altri sono stati uniti non si elimina finché
// loro esistono: l'esito lo dice, con quanti sono.

type Props = {
  tab: TabContext;
  /** La ricerca dell'indirizzo (`cercaArchiviati`), già ripulita; vuota senza ricerca. */
  q: string;
  /** Scrive la ricerca nell'indirizzo: deve restare la stessa funzione fra un disegno e l'altro. */
  onSearch: (q: string | undefined) => void;
  timeZone: string;
  onClose: () => void;
};

/** L'esito di un'azione: i Lead cambiati in una frase, e una riga per ogni Lead non cambiato (o un avviso solo). */
type Outcome = { done: string | null; problems: string[] };

/** Il nome di un archiviato: il nome, il telefono principale o «—». */
function rowName(row: ArchivedLeadRow): string {
  return leadName(row) ?? "—";
}

/** I nomi dei Lead in una frase («Anna Verdi, Bruna Neri»). */
function namesOf(rows: readonly ArchivedLeadRow[]): string {
  return rows.map(rowName).join(", ");
}

/** Le righe di più pagine, una volta sola anche se una pagina riletta ne ha spostata una in quella dopo. */
function distinctRows(pages: ReadonlyArray<{ rows: ArchivedLeadRow[] }>): ArchivedLeadRow[] {
  const seen = new Set<string>();
  const rows: ArchivedLeadRow[] = [];
  for (const row of pages.flatMap((page) => page.rows)) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    rows.push(row);
  }
  return rows;
}

export function ArchivedDialog({ tab, q, onSearch, timeZone, onClose }: Props): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const view = useLeadView();
  const searchId = useId();
  const wordId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const wordRef = useRef<HTMLInputElement>(null);
  const { text, setText } = useResultSearch(q || undefined, onSearch);
  const pages = useInfiniteQuery({ ...archivedLeadsPagesQuery(tab, q, view), placeholderData: keepPreviousData });
  const restore = useRestoreLead(tab);
  const remove = useDeleteLead(tab);
  // I Lead spuntati con la versione letta quando li si è spuntati: uno cambiato dopo (ripristinato e archiviato di
  // nuovo da un collega) non risulta più spuntato, e nemmeno uno che non è più nella pagina.
  const [picked, setPicked] = useState<Readonly<Record<string, number>>>({});
  const [confirming, setConfirming] = useState<"checking" | "ready" | null>(null);
  const [word, setWord] = useState("");
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  // Si comincia dalla ricerca (la finestra altrimenti darebbe il focus a «Chiudi»).
  useEffect(() => {
    searchRef.current?.focus();
  }, []);
  // La domanda della parola, appena c'è, prende il focus.
  useEffect(() => {
    if (confirming === "ready") wordRef.current?.focus();
  }, [confirming]);

  const rows = useMemo(() => distinctRows(pages.data?.pages ?? []), [pages.data]);
  const canDelete = pages.data?.pages[0]?.canDelete ?? false;
  const selected = rows.filter((row) => picked[row.id] === row.version);
  const busy = restore.isPending || remove.isPending || confirming === "checking";
  const wordOk = word.trim().toLocaleLowerCase(locale) === t("leadArchive.deleteWord").toLocaleLowerCase(locale);

  function pick(row: ArchivedLeadRow, checked: boolean) {
    setPicked((current) => {
      const next = { ...current };
      if (checked) next[row.id] = row.version;
      else delete next[row.id];
      return next;
    });
  }

  function unpick(done: readonly ArchivedLeadRow[]) {
    setPicked((current) => {
      const next = { ...current };
      for (const row of done) delete next[row.id];
      return next;
    });
  }

  /**
   * Il motivo per cui un Lead non è cambiato: le parole dell'errore, tranne «Elimina per sempre» di un Lead in cui altri sono
   * stati uniti (`409 invalid_state` con `mergedLeads`), che dice quanti sono e di eliminare prima quelli.
   */
  function failureReason(error: unknown): string {
    const merged = error instanceof ApiRequestError && error.code === "invalid_state" ? error.details?.mergedLeads : undefined;
    if (typeof merged !== "number" || merged < 1) return messageForError(t, error);
    return merged === 1 ? t("leadArchive.mergedLeadsFirstOne") : t("leadArchive.mergedLeadsFirst", { count: formatCount(locale, merged) });
  }

  /** Un avviso solo, senza Lead cambiati. */
  function warn(text: string) {
    setOutcome({ done: null, problems: [text] });
  }

  /**
   * L'esito di un'azione: i nomi di quelli riusciti in una frase, e ogni Lead rifiutato con il suo nome e le parole del suo
   * rifiuto (`failedKey`); `skipped` sono le righe già pronte dei Lead che non sono nemmeno partiti.
   */
  function report(
    result: ArchiveBatchResult,
    doneText: (names: string, count: number) => string,
    failedKey: "leadArchive.restoreFailed" | "leadArchive.deleteFailed",
    skipped: readonly string[] = [],
  ) {
    setOutcome({
      done: result.done.length > 0 ? doneText(namesOf(result.done), result.done.length) : null,
      problems: [...result.failed.map(({ row, error }) => t(failedKey, { name: rowName(row), reason: failureReason(error) })), ...skipped],
    });
  }

  async function restoreRows(targets: readonly ArchivedLeadRow[], skipped: readonly string[] = []) {
    if (busy) return;
    setOutcome(null);
    const result = await restore.mutateAsync(targets);
    unpick(result.done);
    report(result, (names, count) => (count === 1 ? t("leadArchive.restored", { names }) : t("leadArchive.restoredMany", { names })), "leadArchive.restoreFailed", skipped);
  }

  // Si ripristinano soltanto i Lead che la riga del server dice ripristinabili; gli altri spuntati (spuntabili da chi li
  // può eliminare) non partono e si nominano con il motivo: un Lead unito, oppure uno che chi guarda non modifica.
  function restorePicked() {
    if (selected.length === 0) {
      warn(t("leadArchive.pickFirst"));
      return;
    }
    const restorable = selected.filter((row) => row.canRestore);
    const skipped = selected
      .filter((row) => !row.canRestore)
      .map((row) => t("leadArchive.restoreFailed", { name: rowName(row), reason: row.mergedInto ? t("leadArchive.mergedNoRestore") : t("errors.permission_denied") }));
    if (restorable.length === 0) {
      setOutcome({ done: null, problems: skipped });
      return;
    }
    void restoreRows(restorable, skipped);
  }

  // «Elimina per sempre i selezionati»: prima si rileggono gli archiviati, poi si chiede la parola sui Lead come sono
  // adesso (quelli ripristinati o cambiati nel frattempo escono dalla scelta).
  async function askDelete() {
    if (busy) return;
    setOutcome(null);
    if (selected.length === 0) {
      warn(t("leadArchive.pickFirst"));
      return;
    }
    setConfirming("checking");
    setWord("");
    const fresh = await pages.refetch();
    if (fresh.isError || !fresh.data) {
      setConfirming(null);
      warn(messageForError(t, fresh.error));
      return;
    }
    const still = distinctRows(fresh.data.pages).filter((row) => picked[row.id] === row.version);
    if (still.length === 0) {
      setConfirming(null);
      warn(t("leadArchive.pickFirst"));
      return;
    }
    setConfirming("ready");
  }

  async function confirmDelete() {
    if (busy || confirming !== "ready" || !wordOk || selected.length === 0) return;
    const result = await remove.mutateAsync(selected);
    unpick(result.done);
    setConfirming(null);
    setWord("");
    report(result, (names) => t("leadArchive.deleted", { names }), "leadArchive.deleteFailed");
  }

  function cancelDelete() {
    setConfirming(null);
    setWord("");
  }

  let content: JSX.Element;
  if (pages.isError && !pages.data) {
    content = (
      <div className="g-archived__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, pages.error)}
        </Reason>
        <Button onClick={() => void pages.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else if (!pages.data) {
    content = <p role="status">{t("common.loading")}</p>;
  } else if (rows.length === 0 && !pages.isPlaceholderData) {
    content = <p className="g-archived__empty">{q ? t("leadArchive.emptySearch", { q }) : t("leadArchive.empty")}</p>;
  } else {
    content = (
      <>
        <div className="g-table-wrap">
          <table className="g-table g-archived__table" aria-busy={pages.isPlaceholderData || busy ? "true" : undefined}>
            <caption>{t("leadArchive.caption")}</caption>
            <thead>
              <tr>
                <th scope="col" className="g-archived__pick">
                  <span className="g-visually-hidden">{t("leadArchive.columns.pick")}</span>
                </th>
                <th scope="col">{t("leadArchive.columns.lead")}</th>
                <th scope="col">{t("leadArchive.columns.who")}</th>
                <th scope="col">{t("leadArchive.columns.why")}</th>
                <th scope="col">
                  <span className="g-visually-hidden">{t("leadArchive.columns.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <ArchivedRowView
                  key={row.id}
                  t={t}
                  row={row}
                  timeZone={timeZone}
                  // Un Lead che chi guarda non ripristina (unito a un altro, o che non modifica) si spunta soltanto per
                  // eliminarlo per sempre.
                  pickable={canDelete || row.canRestore}
                  picked={picked[row.id] === row.version}
                  disabled={busy}
                  onPick={(checked) => {
                    pick(row, checked);
                    setOutcome(null);
                  }}
                  onRestore={() => void restoreRows([row])}
                />
              ))}
            </tbody>
          </table>
        </div>
        {pages.hasNextPage ? (
          <div>
            <Button onClick={() => void pages.fetchNextPage()} disabled={pages.isFetchingNextPage} aria-busy={pages.isFetchingNextPage ? "true" : undefined}>
              {t("leadArchive.more")}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  const hasRows = rows.length > 0;
  const footer = (
    <>
      {hasRows ? <p className="g-archived__foot-note">{canDelete ? t("leadArchive.deleteWarning") : t("leadArchive.deleteOnlySuperAdmin")}</p> : null}
      {confirming === "ready" ? (
        <div className="g-archived__ask">
          <label className="g-archived__ask-text" htmlFor={wordId}>
            {t("leadArchive.deleteAsk", { count: formatCount(locale, selected.length) })}
          </label>
          <input
            ref={wordRef}
            id={wordId}
            className="g-field__input g-archived__word"
            type="text"
            autoComplete="off"
            spellCheck={false}
            maxLength={20}
            value={word}
            disabled={remove.isPending}
            onChange={(event) => setWord(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              void confirmDelete();
            }}
          />
          <Button variant="primary" disabled={!wordOk || remove.isPending || selected.length === 0} aria-busy={remove.isPending ? "true" : undefined} onClick={() => void confirmDelete()}>
            {t("leadArchive.deleteYes")}
          </Button>
          <Button onClick={cancelDelete} disabled={remove.isPending}>
            {t("leadArchive.no")}
          </Button>
        </div>
      ) : null}
      <p className="g-sheet__status g-archived__status" role="status">
        {outcome?.done ?? null}
      </p>
      {outcome && outcome.problems.length > 0 ? (
        <div className="g-field__error g-archived__problems" role="alert">
          {outcome.problems.length === 1 ? (
            <p>{outcome.problems[0]}</p>
          ) : (
            <ul>
              {outcome.problems.map((line, index) => (
                <li key={`${index}-${line}`}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
      {hasRows ? (
        <Button variant="primary" onClick={restorePicked} disabled={busy} aria-busy={restore.isPending ? "true" : undefined}>
          {t("leadArchive.restorePicked")}
        </Button>
      ) : null}
      {hasRows && canDelete ? (
        <Button onClick={() => void askDelete()} disabled={busy} aria-busy={confirming === "checking" ? "true" : undefined}>
          {t("leadArchive.deletePicked")}
        </Button>
      ) : null}
      <Button onClick={onClose}>{t("common.close")}</Button>
    </>
  );

  return (
    <Dialog open size="table" title={t("leadArchive.title")} onClose={onClose} footer={footer}>
      <p className="g-archived__intro">{t("leadArchive.intro")}</p>
      <div className="g-lead-search g-archived__search">
        <Icon name="search" className="g-lead-search__icon" />
        <label className="g-visually-hidden" htmlFor={searchId}>
          {t("leadArchive.search")}
        </label>
        <input
          ref={searchRef}
          id={searchId}
          className="g-field__input"
          type="search"
          value={text}
          maxLength={SEARCH_MAX_LENGTH}
          autoComplete="off"
          placeholder={t("leadArchive.search")}
          onChange={(event) => {
            setText(event.target.value);
            // Con un'altra ricerca la scelta da confermare cambierebbe sotto gli occhi: la domanda si richiude.
            cancelDelete();
          }}
        />
      </div>
      {content}
    </Dialog>
  );
}

type RowProps = {
  t: Translate;
  row: ArchivedLeadRow;
  timeZone: string;
  pickable: boolean;
  picked: boolean;
  disabled: boolean;
  onPick: (checked: boolean) => void;
  onRestore: () => void;
};

/**
 * Una riga: la casella, il Lead (nome, salone, owner), chi e quando, perché, e «Ripristina» dove la riga del server lo
 * permette, oppure perché un Lead unito non si ripristina.
 */
function ArchivedRowView({ t, row, timeZone, pickable, picked, disabled, onPick, onRestore }: RowProps) {
  const name = rowName(row);
  const owner = row.owner ? t("leadArchive.ownerOf", { name: row.owner.displayName }) : t("leadCard.noOwner");
  const sub = [row.salonName, owner].filter((part): part is string => part !== null && part.trim() !== "").join(" · ");
  let why: string;
  if (row.mergedInto) why = row.mergedInto.displayName ? t("leadArchive.mergedInto", { name: row.mergedInto.displayName }) : t("leadArchive.mergedIntoHidden");
  else why = row.reason?.trim() ? row.reason : "—";
  return (
    <tr>
      <td className="g-archived__pick">
        {pickable ? (
          <input
            type="checkbox"
            className="g-archived__check"
            aria-label={t("leadArchive.pickOne", { name })}
            checked={picked}
            disabled={disabled}
            onChange={(event) => onPick(event.target.checked)}
          />
        ) : null}
      </td>
      <th scope="row">
        <span className="g-archived__name">{name}</span>
        <span className="g-archived__sub">{sub}</span>
      </th>
      <td>
        <span>{row.archivedBy.displayName.trim() || "—"}</span>
        <span className="g-archived__sub">{formatNumericDateTime(timeZone, row.archivedAt)}</span>
      </td>
      <td className="g-archived__why">{why}</td>
      <td>
        {row.mergedInto ? (
          <span className="g-archived__fixed">{t("leadArchive.mergedNoRestore")}</span>
        ) : row.canRestore ? (
          <Button onClick={onRestore} disabled={disabled}>
            {t("leadArchive.restore")}
          </Button>
        ) : null}
      </td>
    </tr>
  );
}
